import {
  type NoCallCsvCheckedRow,
  noCallCsvCheckResponseSchema,
  noCallCsvWriteResponseSchema,
} from "@repo/core/models"
import { z } from "zod"

const PHONE_REGEX = /^[+\d][\d\s\-()]{9,15}$/
const NO_CALL_TRUE_VALUES = new Set(["true", "1", "yes"])
const NO_CALL_FALSE_VALUES = new Set(["false", "0", "no"])

const parsedCsvRowSchema = z
  .object({
    email: z.preprocess(toTrimmedString, z.email("EMAIL_INVALID").optional()),
    noCall: z.preprocess(
      coerceBoolean,
      z.boolean({ message: "NO_CALL_INVALID" }).optional(),
    ),
    phone: z.preprocess(
      toTrimmedString,
      z.string().regex(PHONE_REGEX, "PHONE_NUMBER_INVALID").optional(),
    ),
    rowIndex: z.number(),
  })
  .check((ctx) => {
    if (!ctx.value.phone && !ctx.value.email) {
      ctx.issues.push({
        code: "custom",
        input: ctx.value,
        message: "PHONE_OR_EMAIL_REQUIRED",
      })
    }
    if (ctx.value.noCall === undefined) {
      ctx.issues.push({
        code: "custom",
        input: ctx.value,
        message: "NO_CALL_REQUIRED",
      })
    }
  })

type ParsedCsvRow = z.infer<typeof parsedCsvRowSchema>

type InvalidParsedRow = {
  email?: string
  errors: string[]
  noCall?: boolean | string
  parseStatus: "invalid"
  phone?: string
  rowIndex: number
}

type ValidParsedRow = {
  parseStatus: "valid"
} & ParsedCsvRow

type ParsedRow = InvalidParsedRow | ValidParsedRow

type CheckedRow = {
  email?: string
  noCall: boolean
  parseStatus: "valid"
  phone?: string
  writeErrors?: string[]
  writeStatus?: "error" | "pending" | "success"
} & NoCallCsvCheckedRow

type InvalidCheckedRow = InvalidParsedRow

type MergedRow = CheckedRow | InvalidCheckedRow

type ImportProgress = {
  completed: number
  errors: number
  total: number
}

const checkResponseSchema = z.object({
  data: noCallCsvCheckResponseSchema,
})

const writeResponseSchema = z.object({
  data: noCallCsvWriteResponseSchema,
})

function toTrimmedString(value: unknown) {
  return typeof value === "string" ? value.trim() : value
}

// z.coerce.boolean() uses Boolean("false")!
function coerceBoolean(value: unknown) {
  if (typeof value === "boolean") {
    return value
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase()

    if (NO_CALL_TRUE_VALUES.has(normalized)) {
      return true
    }

    if (NO_CALL_FALSE_VALUES.has(normalized)) {
      return false
    }
  }

  return value
}

export { checkResponseSchema, parsedCsvRowSchema, writeResponseSchema }

export type {
  CheckedRow,
  ImportProgress,
  InvalidCheckedRow,
  InvalidParsedRow,
  MergedRow,
  ParsedRow,
  ValidParsedRow,
}
